class WaterQualityDiagnosticForm extends HTMLElement {
  static GEO_API_URL = 'https://geo.api.gouv.fr/communes';
  static HUBEAU_API_URL = 'https://hubeau.eaufrance.fr/api/v1/qualite_eau_potable/resultats_dis';
  static ZIPCODE_PATTERN = /^\d{5}$/;
  static PARAMETER_CODES = ['1398', '1399', '1340', '1345', '1302', '1295'];
  static CHLORINE_CODES = new Set(['1398', '1399']);
  static CHLORINE_HIGH_THRESHOLD = 0.3;
  static FIELDS = [
    'code_commune',
    'nom_commune',
    'date_prelevement',
    'code_prelevement',
    'conclusion_conformite_prelevement',
    'conformite_limites_bact_prelevement',
    'conformite_limites_pc_prelevement',
    'conformite_references_bact_prelevement',
    'conformite_references_pc_prelevement',
    'code_parametre',
    'libelle_parametre',
    'resultat_numerique',
    'resultat_alphanumerique',
    'libelle_unite',
    'limite_qualite_parametre',
    'reference_qualite_parametre',
    'nom_distributeur',
  ].join(',');

  constructor() {
    super();
    this.form = null;
    this.zipcodeInput = null;
    this.submitButton = null;
    this.results = null;
    this.translations = {};
    this.abortController = null;
    this.contactAction = window.location.pathname || '/';
    this.isSubmitting = false;
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.zipcodeInput = this.querySelector('#zipcode');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.results = this.querySelector('[data-ref="results"]');
    this.translations = this.#loadTranslations();

    this.#disableNativeNavigation();
    this.addEventListener('submit', this.handleSubmit, true);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.handleSubmit, true);
    this.abortController?.abort();
  }

  async handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.isSubmitting) return;

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const zipcode = (this.zipcodeInput?.value || '').replace(/\s/g, '');
    if (!WaterQualityDiagnosticForm.ZIPCODE_PATTERN.test(zipcode)) {
      this.#renderMessage(this.translations.error_zipcode, 'error');
      this.zipcodeInput?.focus();
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.isSubmitting = true;
    this.#setLoading(true);
    this.#renderMessage(this.translations.loading, 'loading');

    try {
      const communes = await this.#fetchCommunes(zipcode, signal);
      if (!communes.length) {
        this.#renderMessage(this.translations.error_commune, 'error');
        return;
      }

      const analyses = await this.#fetchWaterQuality(communes, signal);
      if (!analyses.length) {
        this.#renderMessage(this.translations.error_no_data, 'error');
        return;
      }

      this.#renderResults(communes, analyses);
      this.#submitContactForm();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#renderMessage(this.translations.error_api, 'error');
    } finally {
      this.isSubmitting = false;
      this.#setLoading(false);
    }
  }

  #disableNativeNavigation() {
    if (!this.form) return;

    this.contactAction = window.location.pathname || this.contactAction;
    this.form.setAttribute('method', 'dialog');
    this.form.submit = () => {};
  }

  #loadTranslations() {
    const node = this.querySelector('[data-ref="translations"]');
    if (!node) return {};

    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  async #fetchCommunes(zipcode, signal) {
    const url = new URL(WaterQualityDiagnosticForm.GEO_API_URL);
    url.searchParams.set('codePostal', zipcode);
    url.searchParams.set('fields', 'nom,code');

    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error('geo_api_error');
    }

    const communes = await response.json();
    return Array.isArray(communes) ? communes : [];
  }

  async #fetchWaterQuality(communes, signal) {
    const codeCommune = communes.map((commune) => commune.code).join(',');
    const results = await Promise.all(
      WaterQualityDiagnosticForm.PARAMETER_CODES.map(async (code) => {
        const url = new URL(WaterQualityDiagnosticForm.HUBEAU_API_URL);
        url.searchParams.set('code_commune', codeCommune);
        url.searchParams.set('code_parametre', code);
        url.searchParams.set('fields', WaterQualityDiagnosticForm.FIELDS);
        url.searchParams.set('size', '1');
        url.searchParams.set('sort', 'desc');

        const response = await fetch(url, { signal });
        if (!response.ok) {
          throw new Error('hubeau_api_error');
        }

        const payload = await response.json();
        return Array.isArray(payload?.data) ? payload.data : [];
      }),
    );

    return results.flat();
  }

  #submitContactForm() {
    if (!this.form) return;

    const formData = new FormData(this.form);
    formData.set('form_type', 'contact');
    formData.set('utf8', '✓');

    fetch(this.contactAction, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      redirect: 'manual',
    }).catch(() => {});
  }

  #summarize(analyses) {
    const sorted = [...analyses].sort(
      (first, second) => new Date(second.date_prelevement) - new Date(first.date_prelevement),
    );
    const latest = sorted[0];
    const parameters = [];
    const seen = new Set();

    for (const analysis of sorted) {
      if (!analysis.code_parametre || seen.has(analysis.code_parametre)) continue;
      seen.add(analysis.code_parametre);
      parameters.push(analysis);
    }

    parameters.sort(
      (first, second) =>
        WaterQualityDiagnosticForm.PARAMETER_CODES.indexOf(first.code_parametre) -
        WaterQualityDiagnosticForm.PARAMETER_CODES.indexOf(second.code_parametre),
    );

    return { latest, parameters };
  }

  #renderResults(communes, analyses) {
    const { latest, parameters } = this.#summarize(analyses);
    const communeName = latest.nom_commune || communes[0]?.nom || '';
    const fragment = document.createDocumentFragment();

    const title = this.#createElement('p', {
      className: 'water-quality-diagnostic-form__title',
      textContent: this.#interpolate(this.translations.result_title, { commune: communeName }),
    });

    if (communes.length > 1) {
      fragment.append(
        this.#createElement('p', {
          className: 'water-quality-diagnostic-form__note',
          textContent: this.#interpolate(this.translations.multiple_communes, {
            commune: communeName,
          }),
        }),
      );
    }

    fragment.append(title);

    if (latest.date_prelevement) {
      fragment.append(
        this.#createMeta(this.translations.last_sample, this.#formatDate(latest.date_prelevement)),
      );
    }

    if (latest.nom_distributeur) {
      fragment.append(this.#createMeta(this.translations.distributor, latest.nom_distributeur));
    }

    if (latest.conclusion_conformite_prelevement) {
      fragment.append(
        this.#createElement('p', {
          className: 'water-quality-diagnostic-form__conclusion',
          textContent: latest.conclusion_conformite_prelevement,
        }),
      );
    }

    const badges = this.#createConformityBadges(latest);
    const chlorineInsight = this.#createChlorineInsight(parameters);

    if (badges || chlorineInsight) {
      const card = this.#createElement('div', { className: 'card' });
      if (badges) card.append(badges);
      if (chlorineInsight) card.append(chlorineInsight);
      fragment.append(card);
    }

    if (parameters.length) {
      const list = this.#createElement('ul', {
        className: 'water-quality-diagnostic-form__parameters',
      });

      parameters.forEach((parameter) => {
        list.append(this.#createParameterItem(parameter));
      });

      fragment.append(
        this.#createElement('p', {
          className: 'water-quality-diagnostic-form__section-title',
          textContent: this.translations.parameters,
        }),
        list,
      );
    }

    fragment.append(
      this.#createElement('p', {
        className: 'water-quality-diagnostic-form__source',
        textContent: this.translations.source,
      }),
    );

    this.#replaceResults(fragment, 'success');
  }

  #createConformityBadges(latest) {
    const entries = [
      ['bact_limits', latest.conformite_limites_bact_prelevement],
      ['pc_limits', latest.conformite_limites_pc_prelevement],
    ].filter(([, code]) => code && code !== 'S');

    if (!entries.length) return null;

    const list = this.#createElement('ul', {
      className: 'water-quality-diagnostic-form__badges',
    });

    entries.forEach(([key, code]) => {
      const item = this.#createElement('li', {
        className: `water-quality-diagnostic-form__badge water-quality-diagnostic-form__badge--${this.#conformityModifier(code)}`,
      });
      item.append(
        this.#createElement('span', { textContent: this.translations[key] }),
        this.#createElement('strong', { textContent: this.#conformityLabel(code) }),
      );
      list.append(item);
    });

    return list;
  }

  #createChlorineInsight(parameters) {
    const chlorine = parameters.find((parameter) => WaterQualityDiagnosticForm.CHLORINE_CODES.has(parameter.code_parametre));
    if (!chlorine || typeof chlorine.resultat_numerique !== 'number') return null;

    const isHigh = chlorine.resultat_numerique >= WaterQualityDiagnosticForm.CHLORINE_HIGH_THRESHOLD;
    return this.#createElement('p', {
      className: `water-quality-diagnostic-form__insight water-quality-diagnostic-form__insight--${isHigh ? 'high' : 'ok'}`,
      textContent: isHigh ? this.translations.chlorine_high : this.translations.chlorine_ok,
    });
  }

  #createParameterItem(parameter) {
    const item = this.#createElement('li', {
      className: 'water-quality-diagnostic-form__parameter',
    });
    const value = parameter.resultat_alphanumerique || parameter.resultat_numerique;
    const unit = parameter.libelle_unite ? ` ${parameter.libelle_unite}` : '';

    item.append(
      this.#createElement('span', {
        className: 'water-quality-diagnostic-form__parameter-name',
        textContent: parameter.libelle_parametre,
      }),
      this.#createElement('span', {
        className: 'water-quality-diagnostic-form__parameter-value',
        textContent: `${value ?? '—'}${unit}`,
      }),
    );

    const threshold = parameter.limite_qualite_parametre || parameter.reference_qualite_parametre;
    if (threshold) {
      item.append(
        this.#createElement('span', {
          className: 'water-quality-diagnostic-form__parameter-limit',
          textContent: `${this.translations.limit} : ${threshold}`,
        }),
      );
    }

    return item;
  }

  #createMeta(label, value) {
    const meta = this.#createElement('p', {
      className: 'water-quality-diagnostic-form__meta',
    });
    meta.append(
      this.#createElement('span', { textContent: `${label} : ` }),
      this.#createElement('strong', { textContent: value }),
    );
    return meta;
  }

  #renderMessage(message, status) {
    const paragraph = this.#createElement('p', {
      className: `water-quality-diagnostic-form__message water-quality-diagnostic-form__message--${status}`,
      textContent: message,
    });
    this.#replaceResults(paragraph, status);
  }

  #replaceResults(content, status) {
    if (!this.results) return;

    this.results.replaceChildren(content);
    this.results.hidden = false;
    this.results.dataset.status = status;
  }

  #setLoading(isLoading) {
    this.form?.setAttribute('aria-busy', String(isLoading));
    if (this.submitButton) this.submitButton.disabled = isLoading;
  }

  #conformityLabel(code) {
    const labels = {
      C: this.translations.conformity_c,
      N: this.translations.conformity_n,
      D: this.translations.conformity_d,
      S: this.translations.conformity_s,
    };
    return labels[code] || code;
  }

  #conformityModifier(code) {
    if (code === 'N') return 'fail';
    if (code === 'D') return 'derogation';
    return 'pass';
  }

  #formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(document.documentElement.lang || 'fr-FR', {
      dateStyle: 'long',
    }).format(date);
  }

  #interpolate(template, values) {
    if (!template) return '';
    return Object.entries(values).reduce(
      (result, [key, value]) => result.replaceAll(`__${key}__`, value),
      template,
    );
  }

  #createElement(tag, properties = {}) {
    const element = document.createElement(tag);
    Object.assign(element, properties);
    return element;
  }
}

if (!customElements.get('water-quality-diagnostic-form')) {
  customElements.define('water-quality-diagnostic-form', WaterQualityDiagnosticForm);
}
